/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

export type CameraFacing = "user" | "environment";

export interface CameraDevice {
  deviceId: string;
  label: string;
}

/**
 * What to ask the camera for, most precise first. A request that names the
 * wanted camera exactly cannot be answered with the camera already in use.
 */
export type CameraRequest =
  | { deviceId: { exact: string } }
  | { facingMode: { exact: CameraFacing } }
  | { facingMode: CameraFacing };

/**
 * iOS answers a bare facingMode of "user" by keeping the camera it already
 * has, so the flip button reaches the back camera and then does nothing.
 * The other camera's device id is what actually switches back.
 *
 * An iPhone lists several back cameras. The plain one wins: the shortest
 * label ("Back Camera" before "Back Triple Camera").
 */
export function oppositeVideoDeviceId(
  devices: readonly CameraDevice[],
  currentDeviceId: string | undefined,
  currentLabel = "",
): string | undefined {
  const cameras = devices.filter((device) => device.deviceId !== "");
  if (cameras.length < 2) return undefined;
  const others = cameras.filter(
    (device) => device.deviceId !== currentDeviceId,
  );
  if (others.length === 0) return undefined;

  const current = cameras.find((device) => device.deviceId === currentDeviceId);
  const currentFacing =
    cameraFacingFromLabel(current?.label ?? "") ??
    cameraFacingFromLabel(currentLabel);
  const wanted =
    currentFacing === "user"
      ? "environment"
      : currentFacing === "environment"
        ? "user"
        : undefined;
  if (wanted !== undefined) {
    const matches = others.filter(
      (device) => cameraFacingFromLabel(device.label) === wanted,
    );
    if (matches.length > 0) {
      return matches.reduce((plainest, device) =>
        device.label.length < plainest.label.length ? device : plainest,
      ).deviceId;
    }
  }

  return others.length === 1 ? others[0].deviceId : undefined;
}

export function cameraSwitchRequests(
  nextFacing: CameraFacing,
  nextDeviceId: string | undefined,
): CameraRequest[] {
  return [
    ...(nextDeviceId === undefined
      ? []
      : [{ deviceId: { exact: nextDeviceId } }]),
    { facingMode: { exact: nextFacing } },
    { facingMode: nextFacing },
  ];
}

/** The picture comes from another camera, not the one that was running. */
export function cameraChanged(
  before: { deviceId?: string; facingMode?: string },
  after: { deviceId?: string; facingMode?: string },
): boolean {
  if (before.deviceId !== undefined && after.deviceId !== undefined) {
    return before.deviceId !== after.deviceId;
  }
  return before.facingMode !== after.facingMode;
}

export function cameraFacingFromLabel(label: string): CameraFacing | undefined {
  const value = label.toLowerCase();
  if (/(back|rear|environment|rück|rueck|hinter)/.test(value)) {
    return "environment";
  }
  if (/(front|user|selfie|vorder)/.test(value)) return "user";
  return undefined;
}
